import { Anchor, Box, Button, Checkbox, Divider } from "@mantine/core";
import { IconBrandGoogle, IconMail, IconUser } from "@tabler/icons-react";
import { Input, PasswordInput } from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { authClient } from "@/lib/auth-clients";
import { orpcClient } from "@/lib/orpc-client";
import { useData } from "@/utility/state/contextAPI";
import { useTranslation } from "react-i18next";

function LoginPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState({
    account: {
      isError: false,
      errorMessage: "",
    },
    password: {
      isError: false,
      errorMessage: "",
    },
    login: {
      isError: false,
      errorMessage: "",
    },
  });
  const [isChecked, setIsChecked] = useState(true);

  const navigate = useNavigate();

  const {
    auth: { contextSetJwtToken },
  } = useData();

  const handleOnChange = (
    value: string,
    changeState: (value: string) => void
  ) => {
    changeState(value);
  };

  const handleGoogleLogin = async () => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

      const redirectUrl = `${apiBaseUrl}connect/google`;

      console.log("Redirecting to Strapi Google OAuth:", redirectUrl);
      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Error with Google login:", error);
      setError((prev) => ({
        ...prev,
        login: {
          isError: true,
          errorMessage: t("login.loginError") || "Google登入失敗",
        },
      }));
    }
  };

  const handleOnSubmit = async (
    account: string,
    password: string,
    isChecked: boolean
  ) => {
    let tempError = error;
    if (!account) {
      tempError = {
        ...tempError,
        account: {
          isError: true,
          errorMessage: t("login.accountRequired"),
        },
      };
    } else {
      tempError = {
        ...tempError,
        account: {
          isError: false,
          errorMessage: "",
        },
      };
    }

    if (!password) {
      tempError = {
        ...tempError,
        password: {
          isError: true,
          errorMessage: t("login.passwordRequired"),
        },
      };
    } else {
      tempError = {
        ...tempError,
        password: {
          isError: false,
          errorMessage: "",
        },
      };
    }

    setError(tempError);

    if (account && password) {
      try {
        const result = await authClient.signIn.email({
          email: account,
          password: password,
        });

        console.log("result", result);

        if (result.error) {
          setError((prev) => ({
            ...prev,
            login: {
              isError: true,
              errorMessage: t("login.loginError"),
            },
          }));
          return console.error("signIn_error", result.error);
        }

        if (isChecked) {
          localStorage.setItem(
            "rememberAccount",
            JSON.stringify({ account, password })
          );
        } else {
          localStorage.removeItem("rememberAccount");
        }

        const { data } = result;
        if (data?.session?.token) {
          // localStorage.setItem("jwtToken", data.session.token);
          // contextSetJwtToken(data.session.token);
        }

        const companies = await orpcClient.company.list({});
        console.log("companies", companies);
        if (companies.length > 0) {
          navigate(`/companyListPage`);
        } else {
          navigate(`/addCompanyListPage`);
        }
      } catch (error) {
        console.error("Error signing in:", error);
        setError((prev) => ({
          ...prev,
          login: {
            isError: true,
            errorMessage: t("login.loginError"),
          },
        }));
      }
    }
  };

  useEffect(() => {
    const rememberAccountStr = localStorage.getItem("rememberAccount");
    if (rememberAccountStr) {
      const rememberAccount = JSON.parse(rememberAccountStr);
      if (rememberAccount?.account && rememberAccount?.password) {
        setAccount(rememberAccount.account);
        setPassword(rememberAccount.password);
      }
    }

    const oauthError = searchParams.get("error");
    if (oauthError === "oauth_failed") {
      setError((prev) => ({
        ...prev,
        login: {
          isError: true,
          errorMessage: t("login.loginError") || "Google登入失敗",
        },
      }));
    }
  }, [searchParams, t]);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="w-[487px] rounded-xl bg-white px-16 py-20">
        <div className="text-6xl font-bold" style={{ color: "#6F6F6F" }}>
          {t("login.title")}
        </div>
        <div className="py-4">
          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            style={{ width: "100%" }}
            leftSection={<IconBrandGoogle size={16} />}
            radius="md"
          >
            {t("login.continueWithGoogle") || "使用 Google 繼續"}
          </Button>
        </div>
        <Divider my="xs" label={t("login.or")} labelPosition="center" />
        <div className="py-4">
          <div style={{ color: "#6F6F6F" }}>{t("login.account")}</div>
          <Input
            onChange={(e) => handleOnChange(e.target.value, setAccount)}
            value={account}
            error={error.account.isError}
            placeholder="Input component"
            leftSection={<IconMail size={16} />}
          />
          <Input.Error>{error.account.errorMessage}</Input.Error>
        </div>
        <div className="py-4">
          <div style={{ color: "#6F6F6F" }}>{t("login.password")}</div>
          <PasswordInput
            onChange={(e) => handleOnChange(e.target.value, setPassword)}
            value={password}
            error={error.password.isError}
            placeholder={t("login.passwordPlaceholder")}
            leftSection={<IconUser size={16} />}
          />
          <Input.Error>{error.password.errorMessage}</Input.Error>
        </div>
        <Input.Error>{error.login.errorMessage}</Input.Error>
        <Button
          onClick={() => handleOnSubmit(account, password, isChecked)}
          variant="filled"
          color="gray"
          style={{ width: "100%" }}
          radius="md"
        >
          {t("login.title")}
        </Button>
        <div className="flex items-center justify-center p-[1rem]">
          <Checkbox
            label={t("login.rememberAccount")}
            onClick={(e) => setIsChecked(e.currentTarget.checked)}
            checked={isChecked}
          />
        </div>
        <div className="flex items-center justify-center p-[1rem]">
          <Anchor
            onClick={() => navigate("/signUp")}
            style={{ color: "#6F6F6F", cursor: "pointer" }}
          >
            {t("login.noAccount")}
          </Anchor>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
